<template functional>
  <div class="inline-flex items-center">
    <!-- eslint-disable vue/no-v-html -->
    <div v-html="$options.methods.getBefore(props)" />
    <!-- eslint-enable vue/no-v-html -->
    <template v-for="button in $options.methods.getButtons(props)">
      <div
        v-if="button.type === 'checkbox'"
        :key="button.id"
      >
        <mia-checkbox
          class="w-6 h-6 shadow cursor-pointer"
          @change="event => $options.methods.onChange(props.row, button, event)"
        />
      </div>
      <div
        v-else
        :key="button.id"
        v-tooltip="button.tooltip || ''"
        class="inline-flex"
      >
        <button
          v-if="$options.methods.isButtonVisible(button, props.row)"
          type="button"
          class="inline-flex items-center h-8 px-3 py-1 ml-3 text-sm transition-all duration-100 ease-out rounded shadow focus:outline-none focus:shadow-outline whitespace-no-wrap"
          :class="$options.methods.getButtonStyleClasses(button, props.row)"
          v-bind="$options.methods.getButtonAttrs(button)"
          @click="$options.methods.getClickHandler(button, props)"
        >
          <mia-streamline-icon
            v-if="$options.methods.hasIcon(button)"
            class="w-5 h-5 fill-current"
            :class="{ 'mr-2': $options.methods.getLabel(button, props) !== '' }"
            :src="$options.methods.getIcon(button)"
          />

          <span v-text="$options.methods.getLabel(button, props)" />
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import clone from 'lodash/clone'
import uniqueId from 'lodash/uniqueId'
import { isEmpty } from '@/helpers/utils'

export default {
  name: 'MiaDatatableActionButtonColumn',

  props: {
    row: {
      type: Object,
      default() {
        return {}
      }
    },

    column: {
      type: Object,
      default() {
        return {}
      }
    }
  },

  methods: {
    getButtons({ row, column }) {
      let { actions, additionalData } = column.meta || {}

      if (typeof additionalData === 'function') {
        additionalData = additionalData()
      } else {
        additionalData = null
      }

      if (typeof actions === 'function') {
        actions = actions(row, additionalData)
      }

      if (!Array.isArray(actions)) return []

      actions = actions.filter((action) => {
        let visible = true
        if (action?.visible !== undefined) {
          if (typeof action?.visible === 'function') {
            visible = action?.visible(row)
          } else {
            visible = action?.visible
          }
        }
        return visible
      })

      return actions.map((action) => {
        if (action.id === undefined) {
          return {
            ...action,
            id: uniqueId('datatable-action-button-')
          }
        }

        return action
      })
    },

    getButtonStyleClasses(button, row) {
      let defaultClasses = [
        'text-blue-500',
        'bg-transparent',
        'border',
        'border-blue-500',
        'hover:text-white',
        'hover:bg-blue-500'
      ]

      if (isEmpty(button.class)) return defaultClasses

      let customClasses = button.class

      if (typeof customClasses === 'function') {
        customClasses = customClasses(row)
      }

      if (typeof customClasses === 'string') {
        customClasses = customClasses.split(' ')
      } else if (typeof customClasses === 'object') {
        const customClassesObject = clone(customClasses)

        customClasses = []

        Object.entries(customClassesObject).forEach(([className, condition]) => {
          if (condition) {
            customClasses.push(className)
          }
        })
      }

      // Setup regular expressions to check for existings of button styling classes
      const textRegex = /^text-(.+)-\d+$/
      const hoverTextRegex = /^hover:text-(.+)-\d+$/
      const borderRegex = /^border(?:-.*)?$/
      const hoverBorderRegex = /^hover:border(?:-.*)?$/
      const backgroundRegex = /^bg-(.+)-\d+$/
      const hoverBackgroundRegex = /^hover:bg-(.+)-\d+$/

      const containsText = customClasses.some(className => textRegex.test(className))
      const containsHoverText = customClasses.some(className => hoverTextRegex.test(className))
      const containsBorder = customClasses.some(className => borderRegex.test(className))
      const containsHoverBorder = customClasses.some(className => hoverBorderRegex.test(className))
      const containsBackground = customClasses.some(className => backgroundRegex.test(className))
      const containsHoverBackground = customClasses.some(className => hoverBackgroundRegex.test(className))

      // Filter out classes that should be overwritten
      if (containsText) {
        defaultClasses = defaultClasses.filter(className => !textRegex.test(className))
      }

      if (containsHoverText) {
        defaultClasses = defaultClasses.filter(className => !hoverTextRegex.test(className))
      }

      if (containsBorder) {
        defaultClasses = defaultClasses.filter(className => !borderRegex.test(className))
        // Re-add "border", as it is intended to be removed by the border regex
        defaultClasses.push('border')
      }

      if (containsHoverBorder) {
        defaultClasses = defaultClasses.filter(className => !hoverBorderRegex.test(className))
      }

      if (containsBackground) {
        defaultClasses = defaultClasses.filter(className => !backgroundRegex.test(className))
      }

      if (containsHoverBackground) {
        defaultClasses = defaultClasses.filter(className => !hoverBackgroundRegex.test(className))
      }

      // Finally, merge the left over default classes with the custom ones
      return defaultClasses.concat(customClasses)
    },

    getButtonAttrs(button) {
      return button.attrs || {}
    },

    getClickHandler(button, props) {
      if (!button.onClick) return () => {}

      return button.onClick(props.row)
    },

    getLabel(button, props) {
      if (typeof button.label === 'function') {
        return button.label(props.row)
      }
      return button.label || ''
    },

    hasIcon(button) {
      return button.icon !== undefined
    },

    getIcon(button) {
      return button.icon
    },

    isButtonVisible(button, row) {
      const callback = button.isVisible || (() => true)

      return callback(row)
    },

    onChange(row, button, event) {
      if (!button.onChange) {
        return
      }

      button.onChange(row, event.target.checked)
    },

    getBefore({ row, column }) {
      if (column.meta.before === undefined) {
        return ''
      }

      if (typeof column.meta.before === 'function') {
        return column.meta.before(row)
      }

      return column.meta.before || ''
    }
  }
}
</script>
